import React, { useState, useCallback, useEffect } from 'react'

import { PrepTimeIcon, ScheduleIcon } from '@toasttab/buffet-pui-icons'
import classnames from 'classnames'

import { FulfillmentType } from 'src/apollo/onlineOrdering'
import { ButtonType } from 'src/apollo/sites'
import { BrandedButton } from 'src/shared/components/common/button'
import {
  Modal,
  ModalContent,
  ModalOverlay
} from 'src/shared/components/common/modal'
import { t } from 'src/translations'

import { ToggleInput } from 'shared/components/common/forms'
import LoadingSpinnerOverlay from 'shared/components/common/loading_spinner/LoadingSpinnerOverlay'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import AnimatedSection from 'public/components/default_template/online_ordering/checkout/AnimatedSection'
import { useFulfillment } from 'public/components/online_ordering/FulfillmentContext'

import ShippingDateCalendar from './ShippingDateCalendar'

interface ScheduleShipmentModalProps {
  isOpen: boolean
  onClose: () => void
  estimatedDays?: number
  dateError?: string | null
}

const ScheduleShipmentModal = ({
  isOpen,
  onClose,
  estimatedDays,
  dateError
}: ScheduleShipmentModalProps) => {
  const {
    selectedFulfillmentType,
    setSelectedFulfillmentType,
    updateCartFulfillment,
    fulfillmentData
  } = useFulfillment()
  const { ecommSettings } = useRestaurant()

  const blackoutConfig =
    ecommSettings?.fulfillmentConfig?.shippingSettings?.blackoutConfig

  const [draftFulfillmentTime, setDraftFulfillmentTime] = useState<
    string | null | undefined
  >()
  const [draftFulfillmentType, setDraftFulfillmentType] =
    useState<FulfillmentType>(selectedFulfillmentType ?? FulfillmentType.Asap)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (isOpen) {
      // If there's a date error, clear the fulfillment time and show scheduled view
      if (dateError) {
        setDraftFulfillmentTime(undefined)
        setDraftFulfillmentType(FulfillmentType.Future)
      } else {
        setDraftFulfillmentTime(
          fulfillmentData?.cartFulfillmentData?.fulfillmentDateTime
        )
        setDraftFulfillmentType(selectedFulfillmentType ?? FulfillmentType.Asap)
      }
    }
  }, [
    isOpen,
    dateError,
    fulfillmentData?.cartFulfillmentData?.fulfillmentDateTime,
    selectedFulfillmentType
  ])

  const handleCancel = useCallback(() => {
    setDraftFulfillmentType(selectedFulfillmentType ?? FulfillmentType.Asap)
    setDraftFulfillmentTime(undefined)
    onClose()
  }, [onClose, selectedFulfillmentType])

  const handleSubmit = useCallback(async () => {
    setSubmitting(true)
    try {
      const success = await updateCartFulfillment({
        fulfillmentType: draftFulfillmentType,
        fulfillmentDateTime: draftFulfillmentTime,
        diningOptionBehavior:
          fulfillmentData?.cartFulfillmentData?.diningOptionBehavior,
        deliveryInfo: fulfillmentData?.cartFulfillmentData?.deliveryInfo
      })
      if (success) {
        setSelectedFulfillmentType(draftFulfillmentType)
        onClose()
      }
    } finally {
      setSubmitting(false)
    }
  }, [
    updateCartFulfillment,
    draftFulfillmentType,
    draftFulfillmentTime,
    fulfillmentData,
    setSelectedFulfillmentType,
    onClose
  ])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      preventOverlayClose
      testId='schedule-shipment-modal'
    >
      <ModalOverlay fadeIn />
      <ModalContent contentClassName='modalContentV2 fullScreenMobile fulfillmentModal scheduleShipmentModal'>
        <div className='modalScheduleShipmentHeader'>
          <h2>{t('shipping-section.schedule-shipment')}</h2>
        </div>
        <div className='modalContent'>
          {dateError && (
            <div className='dateErrorMessage' data-testid='date-error-message'>
              {dateError}
            </div>
          )}
          <div className='radioButtons'>
            <ScheduleOptionButton
              label={t('shipping-section.asap')}
              icon={<PrepTimeIcon size='xs' accessibility='decorative' />}
              onChange={() => {
                setDraftFulfillmentType(FulfillmentType.Asap)
                setDraftFulfillmentTime(null)
              }}
              selected={draftFulfillmentType === FulfillmentType.Asap}
              id='asap'
            />
            <ScheduleOptionButton
              label={t('shipping-section.schedule')}
              icon={<ScheduleIcon size='xs' accessibility='decorative' />}
              onChange={() => setDraftFulfillmentType(FulfillmentType.Future)}
              selected={draftFulfillmentType === FulfillmentType.Future}
              id='scheduled'
            />
          </div>
          <AnimatedSection
            expanded={draftFulfillmentType === FulfillmentType.Future}
            className='fulfillmentTimeDropdownContainer'
          >
            <ShippingDateCalendar
              fulfillmentDate={draftFulfillmentTime}
              setFulfillmentDate={setDraftFulfillmentTime}
              draftFulfillmentType={draftFulfillmentType}
              estimatedDays={estimatedDays}
              blackoutConfig={blackoutConfig}
            />
          </AnimatedSection>
        </div>
        <div className='footer'>
          <BrandedButton
            variant={ButtonType.Secondary}
            onClick={handleCancel}
            className='cancelButton'
          >
            {t('shipping-section.cancel')}
          </BrandedButton>
          <BrandedButton
            variant={ButtonType.Primary}
            onClick={handleSubmit}
            testId='schedule-shipment-confirm'
          >
            {t('shipping-section.confirm')}
          </BrandedButton>
        </div>
        {submitting && <LoadingSpinnerOverlay withSubtleBorderRadius />}
      </ModalContent>
    </Modal>
  )
}

const ScheduleOptionButton = ({
  label,
  icon,
  selected,
  onChange,
  id
}: {
  label: string
  icon: React.ReactElement
  selected: boolean
  onChange: () => void
  id: string
}) => {
  return (
    <div className={classnames('radioButtonOption')}>
      {icon}
      <ToggleInput
        checked={selected}
        onChange={onChange}
        name={label}
        id={id}
        type='radio'
        dataTestId={`input-schedule-${id}`}
      >
        <div>{label}</div>
      </ToggleInput>
    </div>
  )
}

export default ScheduleShipmentModal
